import { useEffect, useRef, useState, type MouseEvent } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'op-theme';
const SWITCH_MS = 480;

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light';
}

function applyTheme(next: Theme) {
  const root = document.documentElement;
  if (next === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {}
}

// Has document.startViewTransition? (Chromium / Safari 18+)
type StartViewTransition = (cb: () => void) => unknown;
function getStartViewTransition(): StartViewTransition | null {
  const fn = (document as unknown as { startViewTransition?: StartViewTransition })
    .startViewTransition;
  return typeof fn === 'function' ? fn.bind(document) : null;
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  const isFirstApply = useRef(true);
  const pendingOriginRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (isFirstApply.current) {
      // Skip animation on initial mount.
      isFirstApply.current = false;
      applyTheme(theme);
      return;
    }

    const root = document.documentElement;
    const origin = pendingOriginRef.current;
    pendingOriginRef.current = null;
    const startViewTransition = getStartViewTransition();

    if (origin) {
      root.style.setProperty('--theme-toggle-x', `${origin.x}px`);
      root.style.setProperty('--theme-toggle-y', `${origin.y}px`);
      // Cover the whole viewport from the click origin (farthest corner).
      const dx = Math.max(origin.x, window.innerWidth - origin.x);
      const dy = Math.max(origin.y, window.innerHeight - origin.y);
      const radius = Math.hypot(dx, dy);
      root.style.setProperty('--theme-toggle-radius', `${radius}px`);
    }

    if (startViewTransition && origin) {
      startViewTransition(() => applyTheme(theme));
    } else {
      // Fallback: token cross-fade (also covers reduced-motion since the class
      // selectors are no-op there if we wanted; here we just apply directly).
      root.classList.add('theme-transitioning');
      window.setTimeout(
        () => root.classList.remove('theme-transitioning'),
        SWITCH_MS
      );
      applyTheme(theme);
    }
  }, [theme]);

  return {
    theme,
    toggle: (event?: MouseEvent<HTMLElement>) => {
      if (event) {
        const rect = event.currentTarget.getBoundingClientRect();
        pendingOriginRef.current = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      }
      setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
    },
  };
}
